import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { useIsSuperUser } from "shared/api/useIsSuperUser";
import {
  groupSuggestionsBySite,
  invalidateAfterFobSync,
  type PhysicalKeySyncSuggestion,
  physicalKeySyncReasonLabel,
  postFobSync,
} from "./physicalKeySyncSuggestions";

function requestErrorMessage(error: unknown): string {
  const err = error as { message?: string; response?: { data?: unknown } } | null;
  const data = err?.response?.data as { message?: unknown } | string | undefined;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object" && typeof data.message === "string") return data.message;
  return err?.message || "The sync couldn't be queued.";
}

function SuggestedSite({
  siteId,
  siteName,
  reasons,
  canSync,
  onQueued,
  onNavigate,
}: {
  siteId: string;
  siteName: string | null;
  reasons: string[];
  canSync: boolean;
  onQueued: () => void;
  onNavigate: () => void;
}) {
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: () => postFobSync(siteId),
    onSuccess: () => {
      invalidateAfterFobSync(queryClient, siteId);
      onQueued();
    },
  });
  const name = siteName || siteId;

  return (
    <li aria-label={name} className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <Link
          className="text-sm font-semibold text-slate-900 hover:text-blue-700 hover:underline"
          onClick={onNavigate}
          to={`/admin/sites/${encodeURIComponent(siteId)}`}
        >
          {name}
        </Link>
        <p className="mt-0.5 text-xs text-slate-500">
          {reasons.map(physicalKeySyncReasonLabel).join(" · ")}
        </p>
        {sync.isError && (
          <p className="mt-1 text-xs text-rose-700">{requestErrorMessage(sync.error)}</p>
        )}
      </div>
      {canSync &&
        (sync.isSuccess ? (
          <span className="text-xs font-medium text-emerald-700" role="status">
            Fobs sync queued
          </span>
        ) : (
          // The click is the confirmation: this dialog already explains what a Fobs sync does.
          <Button
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
            size="sm"
            type="button"
            variant="outline"
          >
            {sync.isPending ? "Syncing..." : "Sync fobs"}
          </Button>
        ))}
    </li>
  );
}

type SuggestedSites = ReturnType<typeof groupSuggestionsBySite>;

function suggestionsKey(sites: SuggestedSites): string {
  return sites.map((site) => `${site.siteId}:${site.reasons.join(",")}`).join("|");
}

/** The dialog's body. Mounted only while the dialog is open, so its queued state starts fresh each time. */
function SuggestionDialogBody({
  sites,
  canSync,
  onClose,
}: {
  sites: SuggestedSites;
  canSync: boolean;
  onClose: () => void;
}) {
  const [queued, setQueued] = React.useState<string[]>([]);
  const allQueued = sites.every((site) => queued.includes(site.siteId));

  return (
    <>
      <DialogHeader>
        <DialogTitle>Existing fobs may need a sync</DialogTitle>
        <DialogDescription>
          Your change was saved. Existing fobs aren't resynced to sister sites automatically, so{" "}
          {sites.length === 1 ? "this site" : "these sites"} may need a Fobs sync.
        </DialogDescription>
      </DialogHeader>
      <ul className="divide-y divide-slate-100">
        {sites.map((site) => (
          <SuggestedSite
            key={site.siteId}
            canSync={canSync}
            onNavigate={onClose}
            onQueued={() =>
              setQueued((previous) =>
                previous.includes(site.siteId) ? previous : [...previous, site.siteId],
              )
            }
            reasons={site.reasons}
            siteId={site.siteId}
            siteName={site.siteName}
          />
        ))}
      </ul>
      {!canSync && (
        <p className="text-sm text-slate-600">Ask a super user to run a Fobs sync.</p>
      )}
      <DialogFooter>
        <Button onClick={onClose} type="button" variant="outline">
          {allQueued ? "Done" : "Not now"}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * Shown after a save (site edit, status change, site creation, property migration) whose response
 * suggests Fobs syncs. Existing fobs aren't resynced automatically: super users can queue a Fobs
 * sync per site from here; others are told to ask one. Nothing is posted unless someone clicks
 * "Sync fobs". Open only while there are suggestions.
 */
export default function PhysicalKeySyncSuggestionDialog({
  suggestions,
  onClose,
}: {
  suggestions: readonly PhysicalKeySyncSuggestion[];
  onClose: () => void;
}) {
  const canSync = useIsSuperUser();
  const sites = groupSuggestionsBySite(suggestions);
  const open = sites.length > 0;
  // Keep showing the last suggestions while the dialog animates closed, rather than an empty list.
  const lastSites = React.useRef(sites);
  if (open) lastSites.current = sites;
  const shownSites = open ? sites : lastSites.current;

  // Always mounted, with `open` following the suggestions, so Radix closes the dialog (and restores the
  // page's pointer events) instead of the dialog being unmounted while open.
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        {/* Keyed by the suggestions, so a new set (e.g. a later save) starts with nothing queued. */}
        <SuggestionDialogBody
          key={suggestionsKey(shownSites)}
          canSync={canSync}
          onClose={onClose}
          sites={shownSites}
        />
      </DialogContent>
    </Dialog>
  );
}
